import React, { useState, useEffect } from "react";
import {
  Modal,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Progress,
  Divider,
  Tooltip,
  Button,
  Alert,
} from "antd";
import moment from "moment-timezone";
import { motion } from "framer-motion";
import {
  ClockCircleOutlined,
  MailOutlined,
  UserOutlined,
  ThunderboltOutlined,
  InfoCircleOutlined,
  CloseOutlined,
  ReloadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import "@assets/css/ProgressDetailModal.css";

const { Title, Text } = Typography;

// Helper function to format time remaining
const formatTimeRemaining = (seconds) => {
  if (!seconds && seconds !== 0) return "Calculating...";
  if (seconds === 0) return "Almost done...";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (remainingSeconds > 0 || parts.length === 0)
    parts.push(`${remainingSeconds}s`);

  return parts.join(" ");
};

// Function to truncate email addresses properly
const truncateEmail = (email, maxLength = 24) => {
  if (!email) return "";

  if (email.length <= maxLength) return email;

  // If email contains @, try to preserve domain part
  if (email.includes("@")) {
    const [local, domain] = email.split("@");

    if (local.length > maxLength - domain.length - 5) {
      // If local part is too long, truncate it
      return `${local.substring(
        0,
        maxLength - domain.length - 5
      )}...@${domain}`;
    }

    return email;
  }

  // Regular truncation for non-email strings
  return `${email.substring(0, maxLength - 3)}...`;
};

const ProgressDetailModal = ({ visible, campaign, progress, onClose }) => {
  // State for countdown timer
  const [timeLeft, setTimeLeft] = useState(null);
  // State for refresh button loading state
  const [refreshing, setRefreshing] = useState(false);
  // State for showing refresh success message
  const [showRefreshMessage, setShowRefreshMessage] = useState(false);

  // Start a countdown timer when modal is visible and we have a next scheduled email
  useEffect(() => {
    if (!visible || !progress?.senderProgress?.nextScheduled) return;

    const scheduledTime = new Date(
      progress.senderProgress.nextScheduled.scheduledTime
    ).getTime();

    const updateTimer = () => {
      const now = new Date().getTime();
      const difference = scheduledTime - now;

      if (difference <= 0) {
        setTimeLeft("Due now");
        return;
      }

      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft(`${minutes}m ${seconds}s`);
    };

    // Update immediately and then every second
    updateTimer();
    const timerId = setInterval(updateTimer, 1000);

    return () => clearInterval(timerId);
  }, [visible, progress]);

  if (!campaign || !progress) return null;

  // Calculate percentage of emails sent by each sender
  const calculateSenderPercentage = (sent) => {
    if (!progress.progress.total) return 0;
    return (sent / progress.progress.total) * 100;
  };

  // Calculate remaining emails
  const remainingEmails = progress.progress.total - progress.progress.sent;

  // Format the email distribution text
  const emailDistribution = `${progress.progress.sent}/${progress.progress.total}`;

  // Handler for refresh button
  const handleRefresh = () => {
    setRefreshing(true);

    // Simulate refresh with timeout
    setTimeout(() => {
      setRefreshing(false);
      setShowRefreshMessage(true);

      // Hide the success message after 3 seconds
      setTimeout(() => {
        setShowRefreshMessage(false);
      }, 3000);
    }, 1000);
  };

  // Function to handle export data
  const handleExportData = () => {
    // This would be implemented to export campaign data
    console.log("Exporting campaign data");
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Button icon={<DownloadOutlined />} onClick={handleExportData}>
            Export Data
          </Button>
          <Button
            type="primary"
            icon={<ReloadOutlined spin={refreshing} />}
            loading={refreshing}
            onClick={handleRefresh}
          >
            Refresh Data
          </Button>
        </div>
      }
      width={800}
      title={
        <Space>
          <MailOutlined />
          <span>Campaign Progress Details - {campaign.name}</span>
        </Space>
      }
      closeIcon={<CloseOutlined style={{ color: "white" }} />}
      destroyOnClose={true}
      className="progress-detail-modal"
    >
      {/* Refresh success message */}
      {showRefreshMessage && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{ marginBottom: "16px" }}
        >
          <Alert
            message="Data refreshed successfully"
            type="success"
            showIcon
            closable
            onClose={() => setShowRefreshMessage(false)}
          />
        </motion.div>
      )}

      {/* Summary Stats Row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <div className="stat-container">
            <div className="stat-label">Completion</div>
            <div className="stat-value">
              <ThunderboltOutlined style={{ color: "#1890ff" }} />
              <span style={{ color: "#1890ff" }}>
                {progress.progress.percentage}%
              </span>
            </div>
            <Progress
              percent={Number(progress.progress.percentage)}
              strokeColor={{
                "0%": "#1890FF",
                "100%": "#52C41A",
              }}
              size="small"
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div className="stat-container">
            <div className="stat-label">Current Rate</div>
            <div className="stat-value">
              <ThunderboltOutlined style={{ color: "#52c41a" }} />
              <span style={{ color: "#52c41a" }}>
                {progress.sendingRate.current}/hr
              </span>
            </div>
            <div className="stat-note">
              Configured: {progress.sendingRate.configured}/hr
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div className="stat-container">
            <div className="stat-label">Total Recipients</div>
            <div className="stat-value">
              <MailOutlined style={{ color: "#722ed1" }} />
              <span style={{ color: "#722ed1" }}>
                {progress.progress.total}
              </span>
            </div>
            <div className="stat-note">Total emails to send</div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <div className="stat-container">
            <div className="stat-label">Active Senders</div>
            <div className="stat-value">
              <UserOutlined style={{ color: "#722ed1" }} />
              <span style={{ color: "#722ed1" }}>
                {progress.sendingRate.totalSenders}
              </span>
            </div>
            <div className="stat-note">
              {progress.progress.sent} emails sent total
            </div>
          </div>
        </Col>
      </Row>

      {/* Campaign Progress Section - Fixed Layout */}
      <div className="campaign-progress-section">
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <div className="section-header">
              <MailOutlined className="icon" />
              <span>Email Distribution</span>
              <span className="distribution-count">{emailDistribution}</span>
            </div>

            <div className="progress-bar">
              <Progress
                percent={Number(progress.progress.percentage)}
                strokeColor="#1890FF"
                showInfo={false}
              />
            </div>

            <div className="email-stats-container">
              <div className="email-stat-box sent">
                <div className="stat-box-value">{progress.progress.sent}</div>
                <div className="stat-box-label">Sent</div>
              </div>
              <div className="email-stat-box remaining">
                <div className="stat-box-value">{remainingEmails}</div>
                <div className="stat-box-label">Remaining</div>
              </div>
            </div>
          </Col>

          <Col xs={24} lg={12}>
            <div className="section-header">
              <ClockCircleOutlined className="icon" />
              <span>Timing</span>
            </div>

            <div className="timing-info">
              <div className="timing-box">
                <div className="timing-label">Remaining Time</div>
                <div className="timing-value">
                  {formatTimeRemaining(progress.timing.estimatedTimeRemaining)}
                </div>
              </div>
              <div className="timing-box">
                <div className="timing-label">Est. Completion</div>
                <div className="timing-value">
                  {progress.timing.estimatedCompletionTime
                    ? moment(progress.timing.estimatedCompletionTime).format(
                        "HH:mm, MMM DD"
                      )
                    : "Calculating..."}
                </div>
              </div>
            </div>

            <div className="started-info">
              <InfoCircleOutlined />
              <span>
                Started:{" "}
                {moment(progress.timing.scheduledFor).format("HH:mm, MMM DD")}
              </span>
            </div>
          </Col>
        </Row>
      </div>

      {/* Sender Progress */}
      <div className="section-header">
        <UserOutlined className="icon" />
        <span>Sender Progress</span>
      </div>

      {progress.senderProgress.senders.map((sender, idx) => {
        // Use effectiveEmailAddress instead of checking IDs
        const displayEmail = sender.effectiveEmailAddress || "Unknown Email";
        const isAlias = sender.useAlias && sender.selectedAlias;

        // Format the display text to avoid incomplete email addresses like in the screenshot
        // Show a more complete and readable version of the email address
        const domainPart = displayEmail.split("@")[1] || "";
        const localPart = displayEmail.split("@")[0] || "";

        // Format as "localpart...@domain.com" for better readability
        const displayText =
          localPart.length > 5
            ? `${localPart.substring(0, 5)}...@${domainPart}`
            : displayEmail;

        const senderType = isAlias ? "Alias" : "Direct";
        const avatarColor = isAlias ? "#722ED1" : "#1890FF";
        // Use first letter of local part as avatar text
        const avatarText = localPart.charAt(0).toUpperCase();

        // Calculate total recipients per sender (evenly distributed)
        const totalPerSender = Math.ceil(
          progress.progress.total / progress.sendingRate.totalSenders
        );
        const sentPerSender = sender.sent || 0;
        const remainingPerSender = Math.max(0, totalPerSender - sentPerSender);

        return (
          <div className="sender-row" key={idx}>
            <div className="sender-info">
              <div
                className="sender-avatar"
                style={{ backgroundColor: avatarColor }}
              >
                {avatarText}
              </div>
              <div className="sender-details">
                <div className="sender-name">
                  <Tooltip title={displayEmail}>{displayText}</Tooltip>
                  <Tag
                    color={isAlias ? "purple" : "blue"}
                    style={{ marginLeft: 8 }}
                  >
                    {senderType}
                  </Tag>
                  {sender.useSharedInbox && (
                    <Tag color="cyan" style={{ marginLeft: 4 }}>
                      Shared Inbox
                    </Tag>
                  )}
                </div>
                <div className="sender-stat">
                  <Space>
                    <span>{sentPerSender} sent</span>
                    <Divider type="vertical" />
                    <span>{remainingPerSender} remaining</span>
                    <Divider type="vertical" />
                    <span style={{ color: "#1890FF" }}>
                      {Math.round((sentPerSender / totalPerSender) * 100)}%
                      complete
                    </span>
                  </Space>
                </div>
              </div>
            </div>
            <div className="progress-bar-container">
              <Progress
                percent={(sentPerSender / totalPerSender) * 100}
                showInfo={false}
                strokeColor={isAlias ? "#722ED1" : "#1890FF"}
                size="small"
              />
            </div>
          </div>
        );
      })}

      {/* Next Scheduled Email with Countdown Timer */}
      {progress.senderProgress.nextScheduled && (
        <div className="next-email-section">
          <div className="section-title">Next Scheduled Email</div>

          <div className="email-grid">
            <div className="email-field">
              <div className="field-label">Recipient</div>
              <Tooltip title={progress.senderProgress.nextScheduled.email}>
                <div className="field-value truncate-text">
                  {progress.senderProgress.nextScheduled.email}
                </div>
              </Tooltip>
            </div>
            <div className="email-field">
              <div className="field-label">Scheduled Time</div>
              <div className="field-value">
                {moment(
                  progress.senderProgress.nextScheduled.scheduledTime
                ).format("HH:mm:ss")}
                {timeLeft && (
                  <div className="countdown-timer">
                    <ClockCircleOutlined /> {timeLeft}
                  </div>
                )}
              </div>
            </div>
            <div className="email-field">
              <div className="field-label">Interval</div>
              <div className="field-value">
                {progress.senderProgress.nextScheduled.minutesBetweenEmails}{" "}
                minute(s)
              </div>
            </div>
          </div>

          {/* Added an estimated completion info with animation */}
          <div
            className="next-email-info"
            style={{ marginTop: "12px", textAlign: "center" }}
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Tag color="rgba(255, 255, 255, 0.2)" style={{ color: "white" }}>
                <ClockCircleOutlined /> Campaign will complete approximately{" "}
                {progress.timing.estimatedCompletionTime
                  ? moment(progress.timing.estimatedCompletionTime).fromNow()
                  : "soon"}
              </Tag>
            </motion.div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ProgressDetailModal;
